import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PenLine, Pencil, Trash2, LogOut, Send } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { useLang } from "@/i18n/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const IG_STRINGS = {
  es: { label: "Última publicación de Instagram", hint: "Pega el enlace de tu publicación (post o reel) y la página la mostrará embebida en la sección de Instagram.", save: "Actualizar enlace", ok: "Enlace actualizado. La página ya muestra tu última publicación.", err: "No se pudo actualizar el enlace.", ph: "https://www.instagram.com/p/…" },
  en: { label: "Latest Instagram post", hint: "Paste your post or reel link and the page will display it embedded in the Instagram section.", save: "Update link", ok: "Link updated. The page now shows your latest post.", err: "Could not update the link.", ph: "https://www.instagram.com/p/…" },
  pt: { label: "Última publicação do Instagram", hint: "Cole o link da sua publicação (post ou reel) e a página a exibirá incorporada na seção de Instagram.", save: "Atualizar link", ok: "Link atualizado. A página já mostra sua última publicação.", err: "Não foi possível atualizar o link.", ph: "https://www.instagram.com/p/…" },
  fr: { label: "Dernière publication Instagram", hint: "Collez le lien de votre publication (post ou reel) et la page l'affichera intégrée dans la section Instagram.", save: "Mettre à jour", ok: "Lien mis à jour. La page affiche déjà votre dernière publication.", err: "Impossible de mettre à jour le lien.", ph: "https://www.instagram.com/p/…" },
};

const inputCls =
  "w-full bg-[#0D0D0E] border border-[#F4F1EA]/15 focus:border-[#D4AF37]/60 outline-none px-4 py-3 text-sm text-[#F4F1EA] placeholder:text-[#5a5a5e] transition-colors duration-300";

const Admin = () => {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const [token, setToken] = useState(() => localStorage.getItem("alagrande_admin_token") || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [posts, setPosts] = useState([]);
  const [form, setForm] = useState({ title: "", excerpt: "", body: "", premium: false });
  const [editingId, setEditingId] = useState(null);

  const IG_TXT = IG_STRINGS[lang] ?? IG_STRINGS.es;
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [igUrl, setIgUrl] = useState("");

  const loadPosts = async () => {
    try {
      const { data } = await axios.get(`${API}/posts`);
      setPosts(data);
    } catch {
      /* sin publicaciones aún */
    }
  };

  const loadIg = async () => {
    try {
      const { data } = await axios.get(`${API}/instagram`);
      setIgUrl(data.url || "");
    } catch {
      /* sin enlace aún */
    }
  };

  const saveIg = async () => {
    try {
      await axios.post(`${API}/instagram`, { url: igUrl.trim() }, auth);
      toast.success(IG_TXT.ok);
      window.dispatchEvent(new Event("posts-changed"));
    } catch {
      toast.error(IG_TXT.err);
    }
  };

  useEffect(() => {
    if (token && open) {
      loadPosts();
      loadIg();
    }
  }, [token, open]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { username: email.trim(), password });
      localStorage.setItem("alagrande_admin_token", data.token);
      setToken(data.token);
      setPassword("");
      toast.success(t("admin.welcome"));
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : t("admin.errLogin"));
    } finally {
      setLoading(false);
    }
  };

  const E_TXT = {
    es: { saved: "Corrección guardada.", saveEdit: "Guardar corrección", editing: "Editando escrito", cancel: "Cancelar edición", premiumLabel: "Contenido premium (requiere suscripción)" },
    en: { saved: "Edit saved.", saveEdit: "Save correction", editing: "Editing piece", cancel: "Cancel editing", premiumLabel: "Premium content (subscription required)" },
    pt: { saved: "Correção salva.", saveEdit: "Salvar correção", editing: "Editando escrito", cancel: "Cancelar edição", premiumLabel: "Conteúdo premium (requer assinatura)" },
    fr: { saved: "Correction enregistrée.", saveEdit: "Enregistrer la correction", editing: "Écrit en cours d'édition", cancel: "Annuler l'édition", premiumLabel: "Contenu premium (abonnement requis)" },
  }[lang] ?? { saved: "Corrección guardada.", saveEdit: "Guardar corrección", editing: "Editando escrito", cancel: "Cancelar edición", premiumLabel: "Contenido premium (requiere suscripción)" };

  const save = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.body.trim()) {
      toast.error(t("admin.errRequired"));
      return;
    }
    setLoading(true);
    try {
      const payload = {
        title: form.title.trim(),
        excerpt: form.excerpt.trim() || `${form.body.trim().slice(0, 180)}…`,
        body: form.body.trim(),
        premium: !!form.premium,
      };
      if (editingId) {
        await axios.put(`${API}/posts/${editingId}`, payload, auth);
        toast.success(E_TXT.saved);
      } else {
        await axios.post(`${API}/posts`, payload, auth);
        toast.success(t("admin.okPublish"));
      }
      setForm({ title: "", excerpt: "", body: "", premium: false });
      setEditingId(null);
      loadPosts();
      window.dispatchEvent(new Event("posts-changed"));
    } catch {
      toast.error(t("admin.errPublish"));
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setForm({ title: p.title, excerpt: p.excerpt, body: p.body, premium: !!p.premium });
    document.querySelector('[data-testid="admin-dialog"]')?.scrollTo(0, 0);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm({ title: "", excerpt: "", body: "" });
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/posts/${id}`, auth);
      setPosts(posts.filter((p) => p.id !== id));
      toast.success(t("admin.deleted"));
    } catch {
      toast.error(t("admin.errDelete"));
    }
  };

  const logout = () => {
    localStorage.removeItem("alagrande_admin_token");
    setToken("");
    toast.success(t("admin.logoutToast"));
  };

  return (
    <>
      <button
        data-testid="admin-pen-button"
        onClick={() => setOpen(true)}
        aria-label="Administración del autor"
        title="Administración"
        className="text-[#8E8E93] hover:text-[#D4AF37] transition-colors duration-300 p-2"
      >
        <PenLine className="w-5 h-5" />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-testid="admin-dialog"
          className="bg-[#141417] border border-[#D4AF37]/25 text-[#F4F1EA] max-w-lg max-h-[85vh] overflow-y-auto"
        >
          <p className="font-code text-[10px] tracking-[0.35em] uppercase text-[#C5A059] mb-1">
            {token ? t("admin.desk") : t("admin.access")}
          </p>
          <DialogTitle className="font-display text-2xl text-[#F4F1EA]">
            {token ? t("admin.titleDesk") : t("admin.titleLogin")}
          </DialogTitle>
          <DialogDescription className="text-[#A0A0A5]">
            {token ? t("admin.descDesk") : t("admin.descLogin")}
          </DialogDescription>

          {!token ? (
            <form onSubmit={login} className="mt-5 space-y-4" data-testid="admin-login-form">
              <input
                data-testid="admin-email-input"
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("admin.user")}
                className={inputCls}
                autoComplete="username"
              />
              <input
                data-testid="admin-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t("admin.pass")}
                className={inputCls}
                autoComplete="current-password"
              />
              {error && (
                <p data-testid="admin-login-error" className="text-sm text-red-400">
                  {error}
                </p>
              )}
              <button
                data-testid="admin-login-submit"
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2.5 px-5 py-3.5 bg-[#D4AF37] text-[#0D0D0E] text-xs tracking-[0.2em] uppercase font-semibold hover:bg-[#E5C158] disabled:opacity-70 transition-colors duration-300"
              >
                <PenLine className="w-4 h-4" />
                {loading ? t("admin.checking") : t("admin.enter")}
              </button>
            </form>
          ) : (
            <div className="mt-5 space-y-7">
              <form onSubmit={save} className="space-y-4" data-testid="admin-publish-form">
                {editingId && (
                  <div className="flex items-center justify-between border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-4 py-2.5" data-testid="admin-editing-banner">
                    <span className="font-code text-[10px] tracking-[0.25em] uppercase text-[#D4AF37]">
                      {E_TXT.editing}
                    </span>
                    <button
                      data-testid="admin-cancel-edit"
                      type="button"
                      onClick={cancelEdit}
                      className="text-[11px] uppercase tracking-[0.15em] text-[#A0A0A5] hover:text-[#E5C158] transition-colors"
                    >
                      {E_TXT.cancel}
                    </button>
                  </div>
                )}
                <input
                  data-testid="admin-post-title"
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder={t("admin.postTitle")}
                  className={inputCls}
                />
                <input
                  data-testid="admin-post-excerpt"
                  type="text"
                  value={form.excerpt}
                  onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
                  placeholder={t("admin.postExcerpt")}
                  className={inputCls}
                />
                <textarea
                  data-testid="admin-post-body"
                  rows={8}
                  value={form.body}
                  onChange={(e) => setForm({ ...form, body: e.target.value })}
                  placeholder={t("admin.postBody")}
                  className={`${inputCls} resize-none leading-relaxed`}
                />
                <label className="flex items-center gap-3 cursor-pointer group" data-testid="admin-premium-option">
                  <Checkbox
                    data-testid="admin-premium-checkbox"
                    checked={form.premium}
                    onCheckedChange={(v) => setForm({ ...form, premium: !!v })}
                    className="border-[#D4AF37]/50 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:text-[#0D0D0E]"
                  />
                  <span className="text-xs tracking-[0.15em] uppercase text-[#A0A0A5] group-hover:text-[#D4AF37] transition-colors">
                    {E_TXT.premiumLabel}
                  </span>
                </label>
                <button
                  data-testid="admin-publish-submit"
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2.5 px-5 py-3.5 bg-[#D4AF37] text-[#0D0D0E] text-xs tracking-[0.2em] uppercase font-semibold hover:bg-[#E5C158] disabled:opacity-70 transition-colors duration-300"
                >
                  <Send className="w-4 h-4" />
                  {loading ? t("admin.publishing") : editingId ? E_TXT.saveEdit : t("admin.publish")}
                </button>
              </form>

              <div data-testid="admin-posts-list">
                <p className="font-code text-[10px] tracking-[0.3em] uppercase text-[#C5A059] mb-3">
                  {t("admin.published")} · {posts.length}
                </p>
                {posts.length === 0 && (
                  <p className="text-sm text-[#8E8E93]">{t("admin.none")}</p>
                )}
                <ul className="space-y-2">
                  {posts.map((p) => (
                    <li
                      key={p.id}
                      className="flex items-center justify-between gap-3 border border-[#F4F1EA]/10 px-4 py-3"
                    >
                      <span className="text-sm text-[#D8D4C8] truncate">{p.title}</span>
                      <span className="flex items-center gap-1 shrink-0">
                        <button
                          data-testid={`admin-edit-${p.id}`}
                          onClick={() => startEdit(p)}
                          aria-label={`Editar ${p.title}`}
                          className="text-[#8E8E93] hover:text-[#D4AF37] transition-colors p-1.5"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          data-testid={`admin-delete-${p.id}`}
                          onClick={() => remove(p.id)}
                          aria-label={`Eliminar ${p.title}`}
                          className="text-[#8E8E93] hover:text-red-400 transition-colors p-1.5"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="border-t border-[#F4F1EA]/10 pt-6" data-testid="admin-instagram-block">
                <p className="font-code text-[10px] tracking-[0.3em] uppercase text-[#C5A059] mb-2.5">
                  {IG_TXT.label}
                </p>
                <p className="text-xs text-[#8E8E93] mb-4 leading-relaxed">{IG_TXT.hint}</p>
                <div className="flex gap-2">
                  <input
                    data-testid="admin-instagram-input"
                    type="url"
                    value={igUrl}
                    onChange={(e) => setIgUrl(e.target.value)}
                    placeholder={IG_TXT.ph}
                    className={inputCls}
                  />
                  <button
                    data-testid="admin-instagram-save"
                    type="button"
                    onClick={saveIg}
                    className="shrink-0 px-4 py-3 bg-[#D4AF37] text-[#0D0D0E] text-[11px] tracking-[0.12em] uppercase font-semibold hover:bg-[#E5C158] transition-colors duration-300"
                  >
                    {IG_TXT.save}
                  </button>
                </div>
              </div>

              <button
                data-testid="admin-logout-button"
                onClick={logout}
                className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[#8E8E93] hover:text-[#D4AF37] transition-colors"
              >
                <LogOut className="w-4 h-4" />
                {t("admin.logout")}
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Admin;
